import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  useWorkflowRunStore,
  WorkflowRunStepOperation,
  WorkflowRunStep,
  AgentWorkflowRunStepOperationType,
  AgentWorkflowRunStepStatus,
  AgentWorkflowRunStepOperationStatus,
  WorkflowRunTask,
} from "@/store/workflowRunStore";
import { useAgentWorkflowActionsStore } from "@/store/agentWorkflowActionsStore";
import {
  getNodeInfoFromDefinition,
  WorkflowNodeType,
} from "./helpers/stepHelpers";
import { shouldCollapseOperation } from "./helpers/actionDisplayConfig";
import { useAgentWorkflowStore } from "@/store/agentsStore";
import { OperationDisplay } from "./OperationDisplay";
import { WorkflowRunViewUserMessage } from "./WorkflowRunViewUserMessage";
import { WorkflowRunViewAssistantMessage } from "./WorkflowRunViewAssistantMessage";
import { WorkflowRunViewRichViewOperation } from "./WorkflowRunViewRichViewOperation";
import { CancelTaskConfirmationDialog } from "@/components/CancelTaskConfirmationDialog";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { WorkflowRunViewCondition } from "./WorkflowRunViewCondition";
import { WorkflowRunViewTrigger } from "./WorkflowRunViewTrigger";
import { WorkflowRunViewReasoning } from "./WorkflowRunViewReasoning";
import { GroupedCollapsedOperations } from "./GroupedCollapsedOperations";
import { GroupedStepLoading } from "./GroupedStepLoading";
import { Loader2, X, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RichActionPanel } from "./RichActionPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  parseRichActionType,
  mapOperationToRichActionData,
  RichActionType,
} from "./helpers/richActionHelpers";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchApi } from "@/lib/api";
import { Asset } from "@/store/quoteStore";
import { AddShopRuleButton } from "@/components/agents/rules/AddShopRuleButton";
import { SendButton } from "@/components/SendButton";

interface WorkflowRunViewWithOperationsProps {
  runId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Type of message item displayed in the workflow run view.
 */
enum MessageItemType {
  USER = "USER",
  ASSISTANT = "ASSISTANT",
  OPERATION = "OPERATION",
  COLLAPSED_OPERATIONS = "COLLAPSED_OPERATIONS",
  CONDITION = "CONDITION",
  RICH_VIEW_OPERATION = "RICH_VIEW_OPERATION",
  TRIGGER = "TRIGGER",
  REASONING = "REASONING",
  STEPS_LOADING = "STEPS_LOADING",
}

export type GroupEntry =
  | {
      type: "operation";
      id: string;
      timestamp: Date;
      operation: WorkflowRunStepOperation;
      task?: WorkflowRunTask;
    }
  | {
      type: "reasoning";
      id: string;
      timestamp: Date;
      content: string;
    };

export interface StepWithOperations {
  step: WorkflowRunStep;
  nodeType: WorkflowNodeType;
  nodeName?: string;
  operations?: Array<{
    operation: WorkflowRunStepOperation;
    task?: WorkflowRunTask;
  }>;
}

interface UserMessageItem {
  type: MessageItemType.USER;
  id: string;
  timestamp: Date;
  content: string;
  senderName: string;
  assets?: Asset[];
}

interface AssistantMessageItem {
  type: MessageItemType.ASSISTANT;
  id: string;
  timestamp: Date;
  content: string;
  senderName: string;
  assets?: Asset[];
}

interface OperationMessageItem {
  type: MessageItemType.OPERATION;
  id: string;
  timestamp: Date;
  operation: WorkflowRunStepOperation;
  task?: WorkflowRunTask;
}

interface CollapsedOperationsMessageItem {
  type: MessageItemType.COLLAPSED_OPERATIONS;
  id: string;
  timestamp: Date;
  groupEntries: GroupEntry[];
  isInProgress: boolean;
}

interface ConditionMessageItem {
  type: MessageItemType.CONDITION;
  id: string;
  timestamp: Date;
  content: string;
}

interface RichViewOperationMessageItem {
  type: MessageItemType.RICH_VIEW_OPERATION;
  id: string;
  timestamp: Date;
  operation: WorkflowRunStepOperation;
  task?: WorkflowRunTask;
  richActionId: RichActionType;
}

interface TriggerMessageItem {
  type: MessageItemType.TRIGGER;
  id: string;
  timestamp: Date;
  operation: WorkflowRunStepOperation;
}

interface ReasoningMessageItem {
  type: MessageItemType.REASONING;
  id: string;
  timestamp: Date;
  content: string;
}

interface StepsLoadingMessageItem {
  type: MessageItemType.STEPS_LOADING;
  id: string;
  timestamp: Date;
  steps: StepWithOperations[];
  hasFailures: boolean;
}

type MessageItem =
  | UserMessageItem
  | AssistantMessageItem
  | OperationMessageItem
  | CollapsedOperationsMessageItem
  | ConditionMessageItem
  | RichViewOperationMessageItem
  | TriggerMessageItem
  | ReasoningMessageItem
  | StepsLoadingMessageItem;

export const WorkflowRunViewWithOperations: React.FC<
  WorkflowRunViewWithOperationsProps
> = ({ runId, isOpen, onClose }) => {
  const {
    getWorkflowRun,
    getRunById,
    getRunState,
    sendAgentChat,
    startPolling,
    stopPolling,
    approveTask,
    denyTask,
    approvingTaskId,
    denyingTaskId,
    retryWorkflowRun,
  } = useWorkflowRunStore();
  const { getAgentById } = useAgentWorkflowStore();
  const { getActionById } = useAgentWorkflowActionsStore();

  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [richActionOperationId, setRichActionOperationId] = useState<
    string | null
  >(null);
  const [mostRecentRichActionId, setMostRecentRichActionId] = useState<
    string | null
  >(null);
  const [richActionPanelOpen, setRichActionPanelOpen] = useState(false);
  const [cancelTaskId, setCancelTaskId] = useState<string | null>(null);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const standardAgentName = useStandardAgentDisplayName();
  const [attachedAssets, setAttachedAssets] = useState<Asset[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const run = runId ? getRunById(runId) : null;
  const runState = runId ? getRunState(runId) : null;

  // Fetch run data when component mounts or runId changes
  useEffect(() => {
    if (runId && isOpen) {
      getWorkflowRun(runId);
    }
  }, [runId, isOpen, getWorkflowRun]);

  // Handle polling for active runs
  useEffect(() => {
    if (!runId || !isOpen || !run) {
      return;
    }

    if (
      run.status === "QUEUED" ||
      run.status === "RUNNING" ||
      run.status === "REQUIRES_REVIEW" ||
      run.status === "WAITING"
    ) {
      startPolling(runId);
    }

    // Cleanup function to stop polling when component unmounts or dependencies change
    return () => {
      stopPolling(runId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, isOpen, run?.status, startPolling, stopPolling]);

  const agent = run?.workflowVersion
    ? getAgentById(run.workflowVersion.agentWorkflowId)
    : null;

  // Convert workflow run to message items
  const messageItems = useMemo(() => {
    if (!run || !run.steps) {
      return [];
    }

    const items: MessageItem[] = [];
    const collapsedOperationsByStep: Map<
      string,
      Array<{
        operation: WorkflowRunStepOperation;
        task?: WorkflowRunTask;
        timestamp: Date;
      }>
    > = new Map();
    const stepLoadingItems: Array<{
      step: WorkflowRunStep;
      nodeType: WorkflowNodeType;
      nodeName?: string;
      timestamp: Date;
    }> = [];

    const allOperations = run.steps.flatMap((step) =>
      step.operations.map((operation) => ({ step, operation })),
    );
    const sortedOperations = allOperations.sort(
      (a, b) =>
        new Date(a.operation.createdAt).getTime() -
        new Date(b.operation.createdAt).getTime(),
    );

    run.steps.forEach((step) => {
      const nodeInfo = getNodeInfoFromDefinition(
        step.nodeId,
        run.workflowVersion?.definition,
      );

      const shouldShowStepLoading = shouldShowStepLoadingIndicator(
        step,
        nodeInfo?.type,
      );

      if (shouldShowStepLoading && nodeInfo) {
        const actionName = nodeInfo.actionId
          ? getActionById(nodeInfo.actionId)?.name
          : undefined;

        stepLoadingItems.push({
          step,
          nodeType: nodeInfo.type,
          nodeName: actionName,
          timestamp: new Date(step.updatedAt),
        });
      }
    });

    // Add steps with collapsed operations that weren't already in stepLoadingItems
    collapsedOperationsByStep.forEach((_operations, stepId) => {
      const step = run.steps?.find((s) => s.id === stepId);
      if (!step) {
        return;
      }

      const alreadyInStepLoading = stepLoadingItems.some(
        (item) => item.step.id === stepId,
      );
      if (alreadyInStepLoading) {
        return;
      }

      const nodeInfo = getNodeInfoFromDefinition(
        step.nodeId,
        run.workflowVersion?.definition,
      );

      if (
        !nodeInfo ||
        nodeInfo.type === "TRIGGER" ||
        step.status === AgentWorkflowRunStepStatus.SUCCEEDED ||
        step.status === AgentWorkflowRunStepStatus.CANCELLED
      ) {
        return;
      }

      const actionName = nodeInfo.actionId
        ? getActionById(nodeInfo.actionId)?.name
        : undefined;

      stepLoadingItems.push({
        step,
        nodeType: nodeInfo.type,
        nodeName: actionName,
        timestamp: new Date(step.updatedAt),
      });
    });

    let groupIndex = 0;
    let currentGroup: CollapsedOperationsMessageItem | null = null;

    const flushGroup = () => {
      if (!currentGroup) {
        return;
      }
      items.push(currentGroup);
      currentGroup = null;
    };

    const pushGroupEntry = (entry: GroupEntry) => {
      if (!currentGroup) {
        currentGroup = {
          id: `grouped-collapsed-operations-${groupIndex}`,
          type: MessageItemType.COLLAPSED_OPERATIONS,
          groupEntries: [entry],
          timestamp: entry.timestamp,
          isInProgress: false,
        };
        groupIndex += 1;
        return;
      }

      currentGroup.groupEntries.push(entry);
      if (entry.timestamp.getTime() < currentGroup.timestamp.getTime()) {
        currentGroup.timestamp = entry.timestamp;
      }
    };

    sortedOperations.forEach(({ step, operation }) => {
      const params = operation.operationParameters;
      const operationTask = operation.task;
      const timestamp = new Date(operation.createdAt);

      switch (params.type) {
        case AgentWorkflowRunStepOperationType.USER_MESSAGE: {
          flushGroup();
          const userMessageItem: UserMessageItem = {
            id: operation.id,
            type: MessageItemType.USER,
            content: params.message,
            timestamp,
            senderName: params.user
              ? `${params.user.firstName || ""} ${params.user.lastName || ""}`.trim() ||
                params.user.email
              : params.userName
                ? `${params.userName} (${params.userEmail})`
                : params.userEmail
                  ? `${params.userEmail}`
                  : "User",
            assets: params.assets,
          };
          items.push(userMessageItem);
          break;
        }

        case AgentWorkflowRunStepOperationType.ASSISTANT_MESSAGE: {
          flushGroup();
          const assistantMessageItem: AssistantMessageItem = {
            id: operation.id,
            type: MessageItemType.ASSISTANT,
            content: params.message,
            timestamp,
            senderName: agent?.name || "Assistant",
            assets: params.assets,
          };
          items.push(assistantMessageItem);
          break;
        }

        case AgentWorkflowRunStepOperationType.TOOL_CALL: {
          const richActionId = parseRichActionType(params.actionId);
          const requiresReview =
            operation.status ===
              AgentWorkflowRunStepOperationStatus.REQUIRES_REVIEW ||
            operationTask?.status === "PENDING_REVIEW";
          const shouldCollapse =
            shouldCollapseOperation(params.actionId) && !requiresReview;

          if (richActionId) {
            flushGroup();
            const richViewOperationItem: RichViewOperationMessageItem = {
              id: operation.id,
              type: MessageItemType.RICH_VIEW_OPERATION,
              operation,
              task: operationTask ?? undefined,
              timestamp,
              richActionId,
            };
            items.push(richViewOperationItem);
          } else if (shouldCollapse) {
            const stepOperations = collapsedOperationsByStep.get(step.id) || [];
            stepOperations.push({
              operation,
              task: operationTask ?? undefined,
              timestamp,
            });
            collapsedOperationsByStep.set(step.id, stepOperations);

            if (
              step.status === AgentWorkflowRunStepStatus.SUCCEEDED ||
              step.status === AgentWorkflowRunStepStatus.REQUIRES_REVIEW
            ) {
              const groupEntry: GroupEntry = {
                type: "operation",
                id: operation.id,
                timestamp,
                operation,
                task: operationTask ?? undefined,
              };
              pushGroupEntry(groupEntry);
            }
          } else {
            flushGroup();
            const operationItem: OperationMessageItem = {
              id: operation.id,
              type: MessageItemType.OPERATION,
              operation,
              task: operationTask ?? undefined,
              timestamp,
            };
            items.push(operationItem);
          }
          break;
        }

        case AgentWorkflowRunStepOperationType.CONDITION_CHOICE: {
          flushGroup();
          const conditionItem: ConditionMessageItem = {
            id: operation.id,
            type: MessageItemType.CONDITION,
            content: `Condition: ${params.condition}`,
            timestamp,
          };
          items.push(conditionItem);
          break;
        }

        case AgentWorkflowRunStepOperationType.TRIGGER: {
          flushGroup();
          const triggerItem: TriggerMessageItem = {
            id: operation.id,
            type: MessageItemType.TRIGGER,
            operation,
            timestamp,
          };
          items.push(triggerItem);
          break;
        }

        case AgentWorkflowRunStepOperationType.ASSISTANT_REASONING: {
          if (
            step.status === AgentWorkflowRunStepStatus.SUCCEEDED ||
            (step.status === AgentWorkflowRunStepStatus.REQUIRES_REVIEW &&
              params.reasoning)
          ) {
            const groupEntry: GroupEntry = {
              type: "reasoning",
              id: operation.id,
              timestamp,
              content: params.reasoning,
            };
            pushGroupEntry(groupEntry);
          }
          break;
        }
      }
    });

    flushGroup();

    const runningSteps = stepLoadingItems.filter(
      (item) => item.step.status === AgentWorkflowRunStepStatus.RUNNING,
    );
    const failedSteps = stepLoadingItems.filter(
      (item) => item.step.status === AgentWorkflowRunStepStatus.FAILED,
    );

    // Create grouped item for running steps
    if (runningSteps.length > 0) {
      const stepsLoadingItem: StepsLoadingMessageItem = {
        id: `grouped-step-loading-running`,
        type: MessageItemType.STEPS_LOADING,
        steps: runningSteps.map(({ step, nodeType, nodeName }) => ({
          step,
          nodeType,
          nodeName,
          operations: collapsedOperationsByStep
            .get(step.id)
            ?.map(({ operation, task }) => ({ operation, task })),
        })),
        timestamp: runningSteps[0].timestamp,
        hasFailures: false,
      };
      items.push(stepsLoadingItem);
    }

    // Create grouped item for failed steps
    if (failedSteps.length > 0) {
      const stepsLoadingItem: StepsLoadingMessageItem = {
        id: `grouped-step-loading-failed`,
        type: MessageItemType.STEPS_LOADING,
        steps: failedSteps.map(({ step, nodeType, nodeName }) => ({
          step,
          nodeType,
          nodeName,
          operations: collapsedOperationsByStep
            .get(step.id)
            ?.map(({ operation, task }) => ({ operation, task })),
        })),
        timestamp: failedSteps[0].timestamp,
        hasFailures: true,
      };
      items.push(stepsLoadingItem);
    }

    // Sort by timestamp
    return items.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [run, agent?.name, getActionById]);

  /**
   * Determines whether to show a step loading indicator.
   * Shows when: step is RUNNING with no operations or only ASSISTANT_REASONING ops,
   * or when step is RUNNING with only collapsed tool calls,
   * or when step is FAILED with a failureReason.
   * Does NOT show for: TRIGGER nodes, steps with completed operations, or SUCCEEDED steps.
   */
  function shouldShowStepLoadingIndicator(
    step: WorkflowRunStep,
    nodeType: WorkflowNodeType | undefined,
  ): boolean {
    if (!nodeType || nodeType === "TRIGGER") {
      return false;
    }

    if (
      step.status === AgentWorkflowRunStepStatus.SUCCEEDED ||
      step.status === AgentWorkflowRunStepStatus.CANCELLED
    ) {
      return false;
    }

    if (
      step.status === AgentWorkflowRunStepStatus.FAILED &&
      step.failureReason
    ) {
      return true;
    }

    if (step.status !== AgentWorkflowRunStepStatus.RUNNING) {
      return false;
    }

    return !hasExpandedOperations(step);
  }

  function hasExpandedOperations(step: WorkflowRunStep): boolean {
    return step.operations.some((operation) => {
      const params = operation.operationParameters;
      if (
        params.type === AgentWorkflowRunStepOperationType.ASSISTANT_REASONING
      ) {
        return false;
      }

      if (params.type !== AgentWorkflowRunStepOperationType.TOOL_CALL) {
        return true;
      }

      const requiresReview =
        operation.status ===
          AgentWorkflowRunStepOperationStatus.REQUIRES_REVIEW ||
        operation.task?.status === "PENDING_REVIEW";
      const shouldCollapse =
        shouldCollapseOperation(params.actionId) && !requiresReview;
      return !shouldCollapse;
    });
  }

  const richActionOperation: WorkflowRunStepOperation | undefined =
    useMemo(() => {
      const operations = run?.steps?.flatMap((step) => step.operations) || [];
      return operations.find(
        (operation) => operation.id === richActionOperationId,
      );
    }, [run, richActionOperationId]);

  const richActionTask = useMemo(() => {
    const item = messageItems.find((item) => item.id === richActionOperationId);
    if (
      item &&
      (item.type === MessageItemType.OPERATION ||
        item.type === MessageItemType.RICH_VIEW_OPERATION)
    ) {
      return item.task;
    }
    return undefined;
  }, [messageItems, richActionOperationId]);

  // Auto-open rich action panel for the most recent rich action when it changes
  useEffect(() => {
    const richActionItems = messageItems.filter(
      (item) => item.type === MessageItemType.RICH_VIEW_OPERATION,
    );

    if (richActionItems.length === 0) {
      return;
    }
    // Get the most recent rich action
    const mostRecentRichAction = richActionItems[richActionItems.length - 1];
    const isNew = mostRecentRichAction.id !== mostRecentRichActionId;

    if (isNew) {
      setMostRecentRichActionId(mostRecentRichAction.id);
    }

    // Check if the operation has failed (status may transition after initial open)
    if (
      mostRecentRichAction.type === MessageItemType.RICH_VIEW_OPERATION &&
      mostRecentRichAction.operation.status ===
        AgentWorkflowRunStepOperationStatus.FAILED
    ) {
      // Close the panel if it's showing this failed action
      if (
        richActionOperationId === mostRecentRichAction.id &&
        richActionPanelOpen
      ) {
        setRichActionPanelOpen(false);
      }
      return;
    }

    if (isNew) {
      setRichActionOperationId(mostRecentRichAction.id);
      setRichActionPanelOpen(true);
    }
  }, [
    messageItems,
    richActionOperationId,
    mostRecentRichActionId,
    richActionPanelOpen,
  ]);

  // Determine if chat is enabled and available
  const isChatEnabled = run?.workflowVersion?.allowsChatReplies === true;
  const isRunActive = run?.status === "QUEUED" || run?.status === "RUNNING";
  const canSendMessages = isChatEnabled;

  // Handle file selection
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    setIsUploadingFile(true);

    for (const file of Array.from(files)) {
      try {
        // Get presigned upload URL
        const response = await fetchApi("api/assets/upload", "POST", {
          body: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
        });

        if (!response.ok || !response.data) {
          continue;
        }

        const data = response.data as { uploadUrl: string; asset: Asset };

        // Upload file to S3
        await fetch(data.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        // Add asset to attached assets
        setAttachedAssets((prev) => [...prev, data.asset]);
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }

    setIsUploadingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle removing an attached asset
  const handleRemoveAsset = (assetId: string) => {
    setAttachedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  };

  // Handle sending messages
  const handleSendMessage = async () => {
    if (
      !run ||
      !canSendMessages ||
      !message.trim() ||
      isSending ||
      isUploadingFile
    ) {
      return;
    }

    setIsSending(true);
    const assetIds =
      attachedAssets.length > 0 ? attachedAssets.map((a) => a.id) : undefined;
    const result = await sendAgentChat(
      run.agentWorkflowId,
      run.id,
      message,
      assetIds,
    );
    if (result) {
      setMessage("");
      setAttachedAssets([]);
      setRichActionPanelOpen(false);
      // Start polling for the run if it's now active
      if (result.status === "QUEUED" || result.status === "RUNNING") {
        startPolling(result.id);
      }
    }
    setIsSending(false);
  };

  const handleApprove = useCallback(
    async (taskId: string) => {
      if (!runId) {
        return;
      }
      const success = await approveTask(taskId, runId);
      if (success) {
        setRichActionPanelOpen(false);
      }
    },
    [approveTask, runId],
  );

  const handleDeny = useCallback((taskId: string) => {
    setCancelTaskId(taskId);
  }, []);

  const confirmDeny = useCallback(async () => {
    if (!runId || !cancelTaskId) {
      return;
    }
    const success = await denyTask(cancelTaskId, runId);
    setCancelTaskId(null);
    if (success) {
      setRichActionPanelOpen(false);
    }
  }, [denyTask, runId, cancelTaskId]);

  const handleRetry = useCallback(() => {
    if (!runId) {
      return;
    }
    retryWorkflowRun(runId);
  }, [retryWorkflowRun, runId]);

  const handleOnClose = useCallback(() => {
    setRichActionPanelOpen(false);
    onClose();
  }, [onClose, setRichActionPanelOpen]);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleOnClose();
        }
      }}
    >
      <SheetContent className={cn("w-screen overflow-hidden p-0")}>
        <div className="flex h-full workflow-run-container relative">
          <div
            className={cn(
              "flex flex-col transition-all duration-300 ease-in-out overflow-hidden",
              isMobile
                ? "w-full"
                : isChatCollapsed && richActionPanelOpen
                  ? "w-0"
                  : richActionPanelOpen
                    ? "w-[30%]"
                    : "w-full",
            )}
          >
            <SheetHeader className="px-6 py-2 border-b h-16">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {run?.name ? (
                    <SheetTitle className="text-md text-left">
                      {run.name}
                    </SheetTitle>
                  ) : (
                    <Skeleton className="w-20 h-4" />
                  )}

                  {agent && agent.name ? (
                    <SheetDescription>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm">{agent.name}</span>
                      </div>
                    </SheetDescription>
                  ) : (
                    <Skeleton className="w-20 h-4 mt-1" />
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleOnClose}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-hidden">
              {runState?.getError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-sm text-destructive">
                      Failed to load agent conversation. Please try again or
                      contact support.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {runState.getError}
                    </p>
                  </div>
                </div>
              ) : isRunActive && messageItems.length === 0 ? (
                <div className="flex items-center justify-center h-full flex-col">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">
                    Working...
                  </p>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  <div className="h-full w-full overflow-y-auto overflow-x-hidden">
                    <div className="space-y-4 py-4 px-6">
                      {messageItems.map((item) => {
                        if (item.type === MessageItemType.USER) {
                          return (
                            <WorkflowRunViewUserMessage
                              key={item.id}
                              id={item.id}
                              senderName={item.senderName}
                              content={item.content}
                              assets={item.assets}
                              timestamp={item.timestamp}
                            />
                          );
                        }

                        if (item.type === MessageItemType.ASSISTANT) {
                          return (
                            <WorkflowRunViewAssistantMessage
                              key={item.id}
                              id={item.id}
                              senderName={item.senderName}
                              content={item.content}
                              assets={item.assets}
                              timestamp={item.timestamp}
                            />
                          );
                        }

                        if (item.type === MessageItemType.OPERATION) {
                          return (
                            <div key={item.id} className="pl-11">
                              <OperationDisplay
                                operation={item.operation}
                                task={item.task}
                                onApprove={handleApprove}
                                onDeny={handleDeny}
                                isApproving={approvingTaskId === item.task?.id}
                                isDenying={denyingTaskId === item.task?.id}
                              />
                            </div>
                          );
                        }

                        if (
                          item.type === MessageItemType.COLLAPSED_OPERATIONS
                        ) {
                          return (
                            <GroupedCollapsedOperations
                              key={item.id}
                              groupEntries={item.groupEntries}
                              isInProgress={item.isInProgress}
                            />
                          );
                        }

                        if (item.type === MessageItemType.RICH_VIEW_OPERATION) {
                          return (
                            <WorkflowRunViewRichViewOperation
                              key={item.id}
                              id={item.id}
                              operation={item.operation}
                              task={item.task}
                              richActionId={item.richActionId}
                              onOpenRichActionPanel={(operationId) => {
                                setRichActionOperationId(operationId);
                                setRichActionPanelOpen(!richActionPanelOpen);
                              }}
                              richActionPanelOpen={richActionPanelOpen}
                              onApprove={handleApprove}
                              onDeny={handleDeny}
                              isApproving={approvingTaskId === item.task?.id}
                              isDenying={denyingTaskId === item.task?.id}
                            />
                          );
                        }

                        if (item.type === MessageItemType.CONDITION) {
                          return (
                            <WorkflowRunViewCondition
                              key={item.id}
                              id={item.id}
                              content={item.content}
                            />
                          );
                        }

                        if (item.type === MessageItemType.TRIGGER) {
                          return (
                            <WorkflowRunViewTrigger
                              key={item.id}
                              id={item.id}
                              operation={item.operation}
                            />
                          );
                        }

                        if (item.type === MessageItemType.REASONING) {
                          return (
                            <WorkflowRunViewReasoning
                              key={item.id}
                              id={item.id}
                              content={item.content}
                            />
                          );
                        }

                        if (item.type === MessageItemType.STEPS_LOADING) {
                          return (
                            <GroupedStepLoading
                              key={item.id}
                              steps={item.steps}
                              hasFailures={item.hasFailures}
                            />
                          );
                        }

                        return null;
                      })}

                      {isRunActive && messageItems.length > 0 && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      )}

                      {run?.status === "WAITING" && (
                        <div className="flex items-center justify-center py-4 flex-col gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Agent is waiting...
                          </p>
                          {run.waitUntil && (
                            <p className="text-xs text-muted-foreground">
                              Will resume at{" "}
                              {new Date(run.waitUntil).toLocaleString(
                                undefined,
                                {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                },
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {run?.status === "WAITING" && (
                    <div className="border-t px-6 py-3">
                      <div className="text-sm text-muted-foreground">
                        Send a message to resume immediately.
                      </div>
                    </div>
                  )}

                  {run?.status === "FAILED" && (
                    <div className="border-t px-6 py-3 flex items-center justify-between gap-3">
                      <div className="text-sm text-muted-foreground">
                        Run failed. Retry to continue from the last failed step.
                      </div>
                      <div className="flex items-center gap-2">
                        {runState?.retryError ? (
                          <span className="text-xs text-destructive">
                            {runState.retryError}
                          </span>
                        ) : null}
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={runState?.isRetrying}
                          onClick={handleRetry}
                        >
                          <span>Retry</span>
                          {runState?.isRetrying ? (
                            <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                          ) : null}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Chat input - Always show when chat is enabled */}
                  {isChatEnabled && (
                    <div className="border-t px-6 py-4">
                      {/* Attached assets preview */}
                      {attachedAssets.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {attachedAssets.map((asset) => (
                            <div
                              key={asset.id}
                              className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-md text-sm"
                            >
                              <span className="truncate max-w-[200px]">
                                {asset.fileName}
                              </span>
                              <button
                                onClick={() => handleRemoveAsset(asset.id)}
                                className="hover:text-destructive"
                                disabled={isSending || isRunActive}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingFile || isSending || isRunActive}
                          className="shrink-0"
                        >
                          {isUploadingFile ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Paperclip className="h-4 w-4" />
                          )}
                        </Button>
                        <Textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onSend={handleSendMessage}
                          canSend={!isRunActive && !isUploadingFile}
                          placeholder={
                            isRunActive
                              ? "Agent is working..."
                              : isUploadingFile
                                ? "Uploading files..."
                                : "Send a reply..."
                          }
                          disabled={isRunActive || isSending}
                          className={cn(
                            "flex-1 min-h-[40px] border rounded-md px-3 py-2",
                            isRunActive && "opacity-50 cursor-not-allowed",
                          )}
                        />
                        <SendButton
                          onClick={handleSendMessage}
                          disabled={
                            !message.trim() ||
                            isSending ||
                            isRunActive ||
                            isUploadingFile
                          }
                          loading={isSending}
                          variant="text"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Draggable Splitter - Hidden on mobile */}
          <div
            className={cn(
              "w-0.5 bg-border relative",
              "flex items-center justify-center group transition-all duration-300 ease-in-out",
              (!richActionPanelOpen || isMobile) &&
                "opacity-0 w-0 pointer-events-none",
            )}
          ></div>

          {/* Rich Action Panel - Slides in from right on mobile, push layout on desktop */}
          <div
            className={cn(
              "flex flex-col h-full overflow-hidden bg-background",
              isMobile
                ? "absolute inset-0 z-50 transition-transform duration-300 ease-in-out"
                : "transition-all duration-300 ease-in-out",
              isMobile && !richActionPanelOpen && "translate-x-full",
              isMobile && richActionPanelOpen && "translate-x-0",
              !isMobile && !richActionPanelOpen && "w-0",
              !isMobile && richActionPanelOpen && isChatCollapsed && "w-full",
              !isMobile && richActionPanelOpen && !isChatCollapsed && "w-[70%]",
            )}
          >
            {(richActionPanelOpen || isMobile) &&
              richActionOperation &&
              (() => {
                const richActionData = mapOperationToRichActionData(
                  richActionOperation,
                  richActionTask,
                );
                if (!richActionData) {
                  return null;
                }
                return (
                  <RichActionPanel
                    onOpenChange={setRichActionPanelOpen}
                    richActionData={richActionData}
                    onApprove={handleApprove}
                    onDeny={handleDeny}
                    approvingTaskId={approvingTaskId}
                    denyingTaskId={denyingTaskId}
                    isChatCollapsed={isChatCollapsed}
                    onToggleChat={() => setIsChatCollapsed(!isChatCollapsed)}
                    isMobile={isMobile}
                  />
                );
              })()}
          </div>
        </div>
        <AddShopRuleButton />
      </SheetContent>

      <CancelTaskConfirmationDialog
        open={cancelTaskId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTaskId(null);
          }
        }}
        onConfirm={confirmDeny}
        agentName={standardAgentName}
      />
    </Sheet>
  );
};
